import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@west-tennessee-consulting/ui";
import { useQuery as useApolloQuery, useMutation as useApolloMutation } from "@apollo/client/react";

// Types for backward compatibility
export interface MspDocument {
  id: string;
  title: string;
  content: string;
  excerpt?: string | null;
  category: string;
  tags?: string[] | null;
  client_id?: string | null;
  company_id?: string | null;
  difficulty?: string | null;
  is_internal: boolean;
  is_active: boolean;
  is_featured: boolean;
  estimated_read_time?: number | null;
  last_accessed_at?: string | null;
  access_count: number;
  created_by: string;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
  shared_with_users?: string[] | null;
  company?: {
    id: string;
    name: string;
    industry?: string | null;
    website?: string | null;
    city?: string | null;
    state?: string | null;
  } | null;
  client?: {
    id: string;
    name: string;
    company_name?: string | null;
    email?: string | null;
  } | null;
  creator?: {
    id: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  updater?: {
    id: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
}

export interface CreateMspDocumentData {
  title: string;
  content: string;
  excerpt?: string;
  category:
    | "procedures"
    | "technical"
    | "best-practices"
    | "client-specific"
    | "troubleshooting"
    | "templates";
  tags?: string[];
  client_id?: string;
  company_id?: string;
  difficulty?: "beginner" | "intermediate" | "advanced";
  is_internal?: boolean;
  is_featured?: boolean;
  estimated_read_time?: number;
  shared_with_users?: string[];
}

export interface UpdateMspDocumentData extends Partial<CreateMspDocumentData> {}
import {
  GetMspDocumentssDocument,
  GetMspDocumentsDocument,
  CreateMspDocumentsDocument,
  UpdateMspDocumentsDocument,
  DeleteMspDocumentsDocument,
  type GetMspDocumentssQuery,
  type GetMspDocumentsQuery,
  type CreateMspDocumentsMutation,
  type UpdateMspDocumentsMutation,
  type DeleteMspDocumentsMutation,
  type GetMspDocumentssQueryVariables,
  type Msp_DocumentsInsertInput,
  type Msp_DocumentsUpdateInput,
} from "@/src/gql/graphql";

/** Message off an unknown `catch` binding, without pretending it is an Error. */
function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// Query keys for React Query
export const mspDocsKeys = {
  all: ["msp-docs"] as const,
  lists: () => [...mspDocsKeys.all, "list"] as const,
  list: (filters: unknown) => [...mspDocsKeys.lists(), filters] as const,
  details: () => [...mspDocsKeys.all, "detail"] as const,
  detail: (id: string) => [...mspDocsKeys.details(), id] as const,
};

/**
 * Get MSP Documents list with pagination and filtering
 */
export function useGetMspDocuments(
  variables: {
    first?: number;
    after?: string;
    filter?: GetMspDocumentssQueryVariables["filter"];
    skip?: boolean;
  } = {}
) {
  const { data, loading, error } = useApolloQuery<GetMspDocumentssQuery>(GetMspDocumentssDocument, {
    variables: {
      first: variables.first || 20,
      after: variables.after,
      filter: variables.filter,
    },
    skip: variables.skip,
  });

  return {
    data,
    isLoading: loading,
    error: error ? new Error(error.message) : null,
  };
}

/**
 * Get single MSP Document by ID
 */
export function useGetMspDocument(id: string) {
  const { data, loading, error } = useApolloQuery<GetMspDocumentsQuery>(GetMspDocumentsDocument, {
    variables: { id },
    skip: !id,
  });

  return {
    data: data?.msp_documentsCollection?.edges?.[0]?.node as MspDocument,
    isLoading: loading,
    error: error ? new Error(error.message) : null,
  };
}

/**
 * Create new MSP Document
 */
export function useCreateMspDocument() {
  const { toast } = useToast();
  const [createMspDocument, { loading }] = useApolloMutation<CreateMspDocumentsMutation>(
    CreateMspDocumentsDocument
  );

  const mutateAsync = async (input: Msp_DocumentsInsertInput) => {
    try {
      const result = await createMspDocument({
        variables: { input },
        refetchQueries: ["GetMspDocumentss"],
      });

      toast({
        title: "Document Created",
        description: "Document has been created successfully.",
      });

      return result.data?.insertIntomsp_documentsCollection?.records?.[0];
    } catch (error) {
      toast({
        title: "Error",
        description: errorMessage(error) || "Failed to create document",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    mutateAsync,
    isPending: loading,
  };
}

/**
 * Update existing MSP Document
 */
export function useUpdateMspDocument() {
  const { toast } = useToast();
  const [updateMspDocument, { loading }] = useApolloMutation<UpdateMspDocumentsMutation>(
    UpdateMspDocumentsDocument
  );

  const mutateAsync = async ({ id, input }: { id: string; input: Msp_DocumentsUpdateInput }) => {
    try {
      const result = await updateMspDocument({
        variables: { id, input },
        refetchQueries: ["GetMspDocumentss", "GetMspDocuments"],
      });

      toast({
        title: "Document Updated",
        description: "Document has been updated successfully.",
      });

      return result.data?.updatemsp_documentsCollection?.records?.[0];
    } catch (error) {
      toast({
        title: "Error",
        description: errorMessage(error),
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    mutateAsync,
    isPending: loading,
  };
}

/**
 * Delete MSP Document
 */
export function useDeleteMspDocument() {
  const { toast } = useToast();
  const [deleteMspDocument, { loading }] = useApolloMutation<DeleteMspDocumentsMutation>(
    DeleteMspDocumentsDocument
  );

  const mutateAsync = async (id: string) => {
    try {
      await deleteMspDocument({
        variables: { id },
        refetchQueries: ["GetMspDocumentss"],
      });

      toast({
        title: "Document Deleted",
        description: "Document has been deleted successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: errorMessage(error),
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    mutateAsync,
    isPending: loading,
  };
}
